/**
 * StudentNeedsHelpSheet — Wireframe v1
 *
 * Right-side slide-over showing all catch-up topics for a single student on a
 * single assessment. Opens from the 🆘 N badge in the Assessment Status table.
 *
 * Each topic card has:
 *   - "View transcript"     -> opens MissionTranscriptSheet
 *   - "Remedial for this topic" (secondary) -> opens RemedialAssessmentDialog
 *                                              with a single concept_tag,
 *                                              audience scoped to this student
 *
 * Sticky footer:
 *   - "Generate remedial covering all N topics" (primary) -> opens
 *     RemedialAssessmentDialog with concept_tags=[all topics in sheet]
 *
 * Props:
 *   open: boolean
 *   onOpenChange: fn(boolean)
 *   student: { enrollment_id, child_id, name, topics: [...] } | null
 *   assessmentId: string
 *   assessmentTitle: string
 *   defaultDurationMinutes: number
 *   onRemedialCreated: fn(newAssessmentId)
 */
import { useState } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, FileText, Handshake } from 'lucide-react';
import MissionTranscriptSheet from '@/components/teacher/MissionTranscriptSheet';
import RemedialAssessmentDialog from '@/components/RemedialAssessmentDialog';

const SIGNAL_LABEL = {
  auto_hard_cap: 'hard cap',
  student_confused: 'said confused',
  final_judge_no: 'final judge no',
  request_closure: 'requested closure',
};

function signalToLabel(signal, exchanges) {
  const turns = typeof exchanges === 'number' ? `${exchanges} turns` : 'session ended';
  const reason = SIGNAL_LABEL[signal] || 'ended';
  return `${turns} — ${reason}`;
}

function fmtCompleted(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
    });
  } catch { return ''; }
}

export default function StudentNeedsHelpSheet({
  open,
  onOpenChange,
  student,
  assessmentId,
  assessmentTitle,
  defaultDurationMinutes = 20,
  onRemedialCreated,
}) {
  const [transcriptMissionId, setTranscriptMissionId] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const [remedialOpen, setRemedialOpen] = useState(false);
  const [remedialTags, setRemedialTags] = useState([]);

  const topics = student?.topics || [];
  const n = topics.length;

  const openTranscript = (missionId) => {
    if (!missionId) return;
    setTranscriptMissionId(missionId);
    setTranscriptOpen(true);
  };

  const openRemedialOne = (tag) => {
    setRemedialTags([tag]);
    setRemedialOpen(true);
  };

  const openRemedialAll = () => {
    setRemedialTags(topics.map((t) => t.concept_tag).filter(Boolean));
    setRemedialOpen(true);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-md p-0 flex flex-col"
          data-testid="student-needs-help-sheet"
        >
          <SheetHeader className="p-5 border-b bg-gradient-to-b from-rose-50/60 to-white">
            <SheetTitle className="flex items-center gap-2">
              <Handshake size={18} className="text-rose-600" />
              <span data-testid="student-needs-help-title">
                Needs help · {student?.name || 'Student'}
              </span>
            </SheetTitle>
            <SheetDescription className="text-xs text-slate-600">
              {assessmentTitle}
              <br />
              <span className="font-medium text-rose-700">
                {n} topic{n === 1 ? '' : 's'} flagged
              </span>
            </SheetDescription>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {topics.map((t, idx) => {
              const slug = (t.concept_tag || 'topic').replace(/\s+/g, '-').toLowerCase();
              return (
                <div key={`${t.mission_id || t.concept_tag}-${idx}`}>
                  <div className="text-[10px] font-bold tracking-wider uppercase text-slate-500 mb-1.5">
                    Topic {idx + 1}
                  </div>
                  <div
                    className="border border-amber-300 bg-amber-50/60 rounded-lg p-3.5"
                    data-testid={`topic-card-${slug}`}
                  >
                    <div className="flex items-center gap-2 font-semibold text-slate-900 mb-1.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-amber-500" />
                      <span>{t.concept_tag}</span>
                    </div>
                    <div className="text-xs text-slate-700 mb-0.5">
                      {signalToLabel(t.completion_signal, t.exchanges_count)}
                    </div>
                    {t.completed_at && (
                      <div className="text-[11px] text-slate-500 mb-3">
                        Completed: {fmtCompleted(t.completed_at)}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                      {t.mission_id && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={() => openTranscript(t.mission_id)}
                          data-testid={`view-transcript-${slug}`}
                        >
                          <FileText size={13} className="mr-1.5" />
                          View transcript
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-8 border-amber-400 text-amber-800 hover:bg-amber-100"
                        onClick={() => openRemedialOne(t.concept_tag)}
                        data-testid={`remedial-this-topic-${slug}`}
                      >
                        <Sparkles size={13} className="mr-1.5" />
                        Remedial for this topic
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}

            {n === 0 && (
              <div className="text-center text-sm text-slate-500 py-12">
                No catch-up missions for this student.
              </div>
            )}
          </div>

          {n > 0 && (
            <div className="p-4 border-t bg-white">
              <Button
                className="w-full bg-slate-900 hover:bg-slate-800 text-white"
                onClick={openRemedialAll}
                data-testid="remedial-all-topics-btn"
              >
                <Sparkles size={14} className="mr-2" />
                Generate remedial covering all {n} topic{n === 1 ? '' : 's'}
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Read-only transcript sheet — opens nested */}
      <MissionTranscriptSheet
        open={transcriptOpen}
        onOpenChange={setTranscriptOpen}
        missionId={transcriptMissionId}
      />

      {/* Multi-topic remedial dialog, scoped to this single student */}
      <RemedialAssessmentDialog
        open={remedialOpen}
        onOpenChange={setRemedialOpen}
        sourceAid={assessmentId}
        sourceTitle={assessmentTitle}
        conceptTags={remedialTags}
        defaultDurationMinutes={defaultDurationMinutes}
        targetEnrollmentIds={student ? [student.enrollment_id] : []}
        targetStudentLabel={student ? `${student.name} only` : null}
        onCreated={(newAid) => {
          setRemedialOpen(false);
          onOpenChange?.(false);
          if (typeof onRemedialCreated === 'function') onRemedialCreated(newAid);
        }}
      />
    </>
  );
}
